import { p as proxyCustomElement, H, h, F as Fragment, a as Host } from './index.js';
import { g as getAsChild } from './p-CmanOHks.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { p as parseHrefProp } from './p-DlCOw5Kl.js';
import { d as defineCustomElement$7 } from './p-BbTg3Car.js';
import { d as defineCustomElement$6 } from './p-CE-SFMeF.js';
import { d as defineCustomElement$5 } from './p-BZccBFnt.js';
import { d as defineCustomElement$4 } from './p-Da2lAZSv.js';
import { d as defineCustomElement$3 } from './p-FpOQMZxV.js';
import { d as defineCustomElement$2 } from './p-cR96ip-k.js';
import { d as defineCustomElement$1 } from './p-Bn01tsA6.js';

function getParentHtmlWuiList(el) {
    return el.closest('wui-list');
}

const listItemCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border-radius:.25rem;box-sizing:border-box;color:var(--jw-theme-surface-text-default-color);cursor:pointer;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-width:none;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;flex:none;gap:.5rem;height:100%;margin:0;padding:.5rem .75rem;width:100%}:host([icon]) .inner{gap:.75rem}a.inner{color:var(--jw-theme-surface-text-default-color);cursor:pointer;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;text-decoration:none}.prefix{align-items:center;display:flex;justify-content:center}wui-icon{height:.75rem;width:.75rem}:host([variant=nav]) wui-icon{color:var(--jw-theme-surface-text-helper-color);height:1rem;width:1rem}.text{display:flex;flex-direction:column;gap:.125rem;text-align:initial;width:100%}.label{flex:1 1 auto;font-size:.875rem;letter-spacing:-.0004em;line-height:1.25rem;overflow:hidden;text-overflow:ellipsis;-webkit-user-select:none;user-select:none;white-space:nowrap}.description,.label{font-family:Open Sans;font-weight:400}.description{color:var(--jw-theme-surface-text-description-color);display:block;font-size:.75rem;letter-spacing:0;line-height:1rem;white-space:normal}:host([destructive]){color:var(--jw-theme-error-surface-color)}:host(:focus:not([disabled])),:host([active]:not([disabled])),:host([highlight]){background-color:var(--jw-theme-surface-item-active-bg-color);color:var(--jw-theme-surface-item-active-color);outline:none}:host(:focus:not([disabled])) a,:host(:focus:not([disabled])) wui-icon,:host([active]:not([disabled])) a,:host([active]:not([disabled])) wui-icon,:host([highlight]) a,:host([highlight]) wui-icon{color:var(--jw-theme-surface-item-active-color)}:host(:hover:not([disabled]):not([active])){background-color:var(--jw-theme-surface-item-hover-bg-color)}:host([disabled]){color:var(--jw-theme-surface-text-disabled-color);cursor:not-allowed}:host([disabled]) a{color:var(--jw-theme-surface-text-disabled-color);cursor:not-allowed}:host([disabled]) wui-icon{color:var(--jw-theme-surface-text-disabled-color)}:host([disabled]) .description{color:var(--jw-theme-surface-text-disabled-color)}:host([variant=button]){color:var(--jw-theme-system-surface-color)}";

const WuiListItem = /*@__PURE__*/ proxyCustomElement(class WuiListItem extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.focusable = true;
        /**
         * If item can be checked with checkbox
         */
        this.checkable = false;
        /**
         * If destructive action
         */
        this.destructive = false;
        /**
         * If disabled
         */
        this.disabled = false;
        /**
         * If item is checked
         */
        this.checked = false;
        /**
         * If item is active (focused)
         */
        this.active = false;
        /**
         * If item is highlighted
         */
        this.highlight = false;
        this.variant = 'default';
        this._setChecked = (state) => {
            if (!this.checkable || this.disabled) {
                return false;
            }
            if (state === this.checked) {
                return false;
            }
            this.checked = state;
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
            return true;
        };
    }
    _checked(newValue) {
        typeof newValue !== 'undefined' && this._setChecked(newValue);
    }
    _active(newValue) {
        if (!newValue) {
            return;
        }
        if (document.activeElement === this.elm) {
            // Active element should already be in view as it has been focused
            return;
        }
        // Scroll into view only when becoming active
        window.requestAnimationFrame(() => {
            this.elm.scrollIntoView({ block: 'nearest' });
        });
    }
    _handleClick(e) {
        // Prevent clicks from emiting on this element if it's disabled
        if (this.disabled) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return;
        }
        if (this.checkable) {
            this._setChecked(!this.checked);
        }
    }
    _handleFocus() {
        const parentElement = getParentHtmlWuiList(this.elm);
        parentElement?.setActiveItem(this.elm);
    }
    /**
     * Forward a keydown KeyboardEvent to be handled by this component.
     *
     * Handles Enter to select/check items.
     *
     * @param e KeyboardEvent (should be keydown)
     * @returns Returns true when the event was handled
     */
    async handleKeyDown(e) {
        return this._handleKeyDown(e);
    }
    listenKeyDown(e) {
        return this._handleKeyDown(e);
    }
    _handleKeyDown(e) {
        if (e.key === 'Enter') {
            if (this.disabled) {
                // If the element is disabled, we'll allow someone else to handle the event
                return false;
            }
            // Check/uncheck element if possible
            if (this._setChecked(!this.checked)) {
                e.preventDefault();
                e.stopPropagation();
                return true;
            }
            // Emit 'click' event
            this.elm.dispatchEvent(new window.Event('click', { bubbles: true }));
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        return false;
    }
    // Forward focus event to parent wui-list if present
    handleFocus() {
        const parentList = this.elm.closest('wui-list');
        if (parentList) {
            forwardEvent(new Event('focus'), parentList);
        }
    }
    // Forward blur event to parent wui-list if present
    handleBlur() {
        const parentList = this.elm.closest('wui-list');
        if (parentList) {
            forwardEvent(new Event('blur'), parentList);
        }
    }
    render() {
        const aProps = this.as?.[0] === 'a' ? this.as[1] : undefined;
        const isCheckable = this.checkable && !aProps;
        const hasIcon = this.icon && !this.checkable;
        const needsPrefix = isCheckable || hasIcon;
        const content = getAsChild(this.as || ['div', {}], h(Fragment, null, needsPrefix && (h("div", { class: "prefix" }, this.checkable ? (h("wui-check-input", { checked: this.checked, disabled: this.disabled, tabindex: -1, onClick: (e) => {
                // We have our own click event
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
            }, onInput: (e) => {
                // Don't bubble up this event, we have our own click event that handles this.
                e.stopPropagation();
                e.stopImmediatePropagation();
            } })) : (h(Fragment, null, this.icon && h("wui-icon", { name: this.icon }))))), (this.label || typeof this.description === 'string') && (h("div", { class: "text" }, this.label && (h("span", { class: "label" }, typeof this.label === 'string' ? (this.label) : (h(Fragment, null, this.label.map((item, idx) => {
            const [str, style] = Array.isArray(item) ? item : [item];
            if (style === 'bold') {
                return h("b", { key: idx }, str);
            }
            return str;
        }))))), typeof this.description === 'string' && h("div", { class: "description" }, this.description)))), {
            div: {
                class: 'inner',
            },
            a: {
                ...parseHrefProp({
                    target: '_self',
                    ...aProps,
                }, this.disabled),
                class: 'inner',
            },
        });
        return (h(Host, { role: "listitem", tabIndex: this.disabled ? -1 : 0, size: typeof this.description === 'string' ? 'lg' : 'md' }, content));
    }
    get elm() { return this; }
    static get watchers() { return {
        "checked": ["_checked"],
        "active": ["_active"]
    }; }
    static get style() { return listItemCss; }
}, [257, "wui-list-item", {
        "focusable": [516],
        "checkable": [516],
        "label": [1],
        "icon": [513],
        "description": [1],
        "destructive": [516],
        "disabled": [516],
        "checked": [1540],
        "active": [1540],
        "highlight": [516],
        "as": [16],
        "variant": [513],
        "handleKeyDown": [64]
    }, [[2, "click", "_handleClick"], [0, "focus", "_handleFocus"], [0, "keydown", "listenKeyDown"], [2, "focus", "handleFocus"], [2, "blur", "handleBlur"]], {
        "checked": ["_checked"],
        "active": ["_active"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-list-item", "wui-box", "wui-check-input", "wui-form-field", "wui-icon", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiListItem);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiListItem as W, defineCustomElement as d };
